import React, { useState, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    Users,
    LogOut, Building2, Menu, X, ChevronDown, Info,
    User, CreditCard, HelpCircle, Wrench,
    CalendarCheck, MessageSquare, FileText, FileSignature, Kanban, Car,
    ClipboardList, BookOpen,
    Calculator, Truck, Store, Settings,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    logoutUser, logout, selectUser, selectOrganisationProvisioned,
} from '../store/slices/authSlice';
import { UiViewKey } from '../constants/uiViewKeys';

const Layout = ({ children }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useAppSelector(selectUser);
    const organisationProvisioned = useAppSelector(selectOrganisationProvisioned);

    // FALLBACK ONLY. Setup now refreshes the token in place, so this should never
    // normally be true — it covers the case where that refresh failed (offline, refresh
    // token rejected) and the user really does have to sign in again.
    //
    // True only in the gap between finishing workshop setup and signing back in.
    //
    // organisationProvisioned is set in Redux the moment provisioning succeeds, while
    // organisationUuid comes from the JWT — which was minted before the workshop
    // existed and therefore does not carry it. Both conditions together are precisely
    // "the workshop exists but THIS session predates it", which is also why the nav
    // renders empty: it is built from the token's uiViews claim.
    //
    // Signing back in satisfies the second condition, so this clears itself.
    const needsReactivation = Boolean(organisationProvisioned && !user?.organisationUuid);

    // Sidebar closed by default on mobile, open on desktop
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);

    const handleLogout = () => {
        dispatch(logoutUser());
        dispatch(logout());
    };

    // Main navigation items — each tagged with its UiViewKey so it can be
    // filtered against the user's `uiViews` claim from the JWT.
    const allNavItems = [
        // Operations
        { key: UiViewKey.BOOKINGS,                   icon: CalendarCheck,   label: 'Bookings',                   path: '/bookings',            section: 'operations' },
        { key: UiViewKey.QUOTES,                     icon: FileSignature,   label: 'Quotes',                     path: '/quotes',              section: 'operations' },
        { key: UiViewKey.JOBS,                       icon: Kanban,          label: 'Jobs',                       path: '/jobs',                section: 'operations' },
        { key: UiViewKey.INVOICES,                   icon: FileText,        label: 'Invoices',                   path: '/invoices',            section: 'operations' },
        { key: UiViewKey.SERVICE_RECORDS,            icon: BookOpen,        label: 'Service Records',            path: '/service-records',     section: 'operations' },
        { key: UiViewKey.FLEET_CUSTOMERS,            icon: Truck,           label: 'Customers',            path: '/fleet-customers',     section: 'operations' },

        // ── Fleet portal (CLIENT users only) ─────────────────────────────────
        // These render only for fleet customer logins — their UiViewProfile grants
        // exactly these four keys, and workshop users never hold them, so the
        // existing canSee() gating keeps the two navs completely separate.
        { key: UiViewKey.FLEET_VEHICLES,             icon: Car,             label: 'My Vehicles',                path: '/fleet/vehicles',        section: 'operations' },
        { key: UiViewKey.FLEET_BOOKINGS,             icon: CalendarCheck,   label: 'My Bookings',                path: '/fleet/bookings',        section: 'operations' },
        { key: UiViewKey.FLEET_INVOICES,             icon: FileText,        label: 'My Invoices',                path: '/fleet/invoices',        section: 'operations' },
        { key: UiViewKey.FLEET_SERVICE_RECORDS,      icon: BookOpen,        label: 'Service History',            path: '/fleet/service-records', section: 'operations' },
        { key: UiViewKey.FLEET_SETTINGS,             icon: Settings,        label: 'My Details',                 path: '/fleet/settings',        section: 'operations' },

        { key: UiViewKey.CUSTOMER_COMMUNICATIONS,    icon: MessageSquare,   label: 'Customer Communications',    path: '/communications',      section: 'operations' },

        // Resources & Inventory
        { key: UiViewKey.RESOURCE_PLANNING,          icon: Car,             label: 'Resource Planning',          path: '/resources',           section: 'resources' },

        // Configuration
        { key: UiViewKey.WORK_ITEMS,                 icon: Wrench,          label: 'Work Items',                 path: '/work-item-profiles',  section: 'config' },
        { key: UiViewKey.SERVICE_PROFILES,           icon: ClipboardList,   label: 'Service Profiles',           path: '/service-profiles',    section: 'config' },
        { key: UiViewKey.TEAM_MEMBERS,               icon: Users,           label: 'Team Members',               path: '/team',                section: 'config' },
        { key: UiViewKey.ACCOUNTING_SOFTWARE,        icon: Calculator,      label: 'Accounting Software',        path: '/accounting-software', section: 'config' },
        { key: UiViewKey.ORGANISATION,               icon: Building2,       label: 'Organisation',               path: '/organisation',        section: 'config' },
    ];

    // Profile menu items — also gated by UiViewKey.
    const allProfileMenuItems = [
        { key: UiViewKey.MY_PROFILE,    icon: User,        label: 'My Account',     path: '/account' },
        { key: UiViewKey.HELP_SUPPORT,  icon: HelpCircle,  label: 'Help & Support', path: '/help' },
    ];

    // Derive allowed view set from the user's JWT-sourced `uiViews` claim.
    // OWNER users receive the full list from the backend; no special case needed.
    const allowedViews = useMemo(
        () => new Set(user?.uiViews || []),
        [user?.uiViews]
    );

    const canSee = (key) => allowedViews.has(key);

    // Filter into section buckets so we can omit empty sections entirely.
    const operationsItems = allNavItems.filter(i => i.section === 'operations' && canSee(i.key));
    const resourcesItems  = allNavItems.filter(i => i.section === 'resources'  && canSee(i.key));
    const configItems     = allNavItems.filter(i => i.section === 'config'     && canSee(i.key));
    const profileMenuItems = allProfileMenuItems.filter(i => canSee(i.key));

    const NavItem = ({ icon: Icon, label, path, onClick }) => (
        <NavLink
            to={path}
            onClick={onClick}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-transparent'
                }`
            }
        >
            <Icon size={18} />
            <span>{label}</span>
        </NavLink>
    );

    // Section divider component
    const SectionDivider = ({ label }) => (
        <div className="pt-4 pb-2 px-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
        </div>
    );

    // Signed-in user identity — avatar, email, role. Rendered in the sidebar on
    // desktop (under the logo) and in the top bar on mobile, never both at once.
    const UserIdentity = ({ compact }) => (
        <>
            <div className={`relative ${compact ? 'w-8 h-8' : 'w-9 h-9'} bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0`}>
                <span className="text-emerald-700 font-semibold text-sm">
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                </span>
                {/* Sign out is buried in this menu, and at this exact moment the whole
                    nav above is empty — so there is nothing else on screen drawing the
                    eye anywhere useful. The dot bounces over the avatar to point at the
                    one control that finishes activation. */}
                {needsReactivation && (
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500 animate-bounce" />
                    </span>
                )}
            </div>
            {/* On the mobile bar the text is dropped on the narrowest phones —
                the sidebar footer still shows it in full. */}
            <div className={`min-w-0 flex-1 text-left ${compact ? 'hidden sm:block' : ''}`}>
                <p className="text-[13px] font-medium text-gray-900 leading-tight truncate" title={user?.email || 'User'}>
                    {user?.email || 'User'}
                </p>
                <p className="text-[11px] text-gray-500 leading-tight">
                    {user?.staffRole || user?.userType || 'Owner'}
                </p>
            </div>
        </>
    );

    // Dropdown panel shared by both profile buttons — positioning is the caller's
    // job so the sidebar copy can open downward inside a 16rem column.
    const ProfileMenuPanel = ({ className }) => (
        <>
            <div className="fixed inset-0 z-40" onClick={() => setProfileMenuOpen(false)} />
            <div className={`bg-white rounded-xl border border-gray-200 shadow-lg py-2 z-50 ${className}`}>
                {profileMenuItems.length > 0 && (
                    <div className="pb-2">
                        {profileMenuItems.map(({ icon: Icon, label, path }) => (
                            <button
                                key={path}
                                onClick={() => {
                                    setProfileMenuOpen(false);
                                    navigate(path);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors bg-transparent border-none cursor-pointer text-left"
                            >
                                <Icon size={16} className="text-gray-400" />
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                <div className="border-t border-gray-100 pt-2">
                    <button
                        onClick={() => {
                            setProfileMenuOpen(false);
                            handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors bg-transparent border-none cursor-pointer text-left"
                    >
                        <LogOut size={16} />
                        Sign out
                    </button>
                </div>
            </div>
        </>
    );

    // Render a named section only if it has visible items.
    const NavSection = ({ label, items, showDivider }) => {
        if (items.length === 0) return null;
        return (
            <>
                {showDivider && <SectionDivider label={label} />}
                <div className="space-y-1">
                    {items.map((item) => (
                        <NavItem key={item.path} {...item} />
                    ))}
                </div>
            </>
        );
    };

    return (
        <div className="h-screen overflow-hidden bg-gray-50 flex">
            {/* Desktop Sidebar */}
            <aside
                className={`hidden lg:flex fixed lg:static inset-y-0 left-0 z-40 bg-white border-r border-gray-200 transition-all duration-300 w-64 flex-col`}
            >
                {/* Logo */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-display font-bold text-base">W</span>
                        </div>
                        <span className="text-gray-900 font-display font-bold text-xl tracking-tight">
                            wadakaroo
                        </span>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 overflow-y-auto">
                    <NavSection label="Operations" items={operationsItems} showDivider={false} />
                    <NavSection label="Resources & Inventory" items={resourcesItems} showDivider />
                    <NavSection label="Configuration" items={configItems} showDivider />
                </nav>

                {/* Sidebar footer — signed-in user sits with Sign out, at the far
                    end of the nav, so the main area needs no top bar of its own.
                    The menu opens upward because there is nothing below it. */}
                <div className="relative p-3 border-t border-gray-100">
                    <button
                        onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                        className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer"
                    >
                        <UserIdentity />
                        <ChevronDown
                            size={16}
                            className={`text-gray-400 flex-shrink-0 transition-transform ${
                                profileMenuOpen ? '' : 'rotate-180'
                            }`}
                        />
                    </button>

                    {/* Sign out lives inside this menu only — a second standalone
                        button alongside it was pure duplication. */}
                    {profileMenuOpen && (
                        <ProfileMenuPanel className="absolute left-3 right-3 bottom-full mb-1" />
                    )}
                </div>
            </aside>

            {/* Mobile Sidebar */}
            <div
                className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
                    mobileSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                }`}
            >
                {/* Overlay */}
                <div
                    className="absolute inset-0 bg-black/50"
                    onClick={() => setMobileSidebarOpen(false)}
                />

                {/* Sidebar panel */}
                <aside
                    className={`absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-xl transition-transform duration-300 flex flex-col ${
                        mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
                >
                    {/* Logo */}
                    <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-display font-bold text-base">W</span>
                            </div>
                            <span className="text-gray-900 font-display font-bold text-xl tracking-tight">
                                wadakaroo
                            </span>
                        </div>
                        <button
                            onClick={() => setMobileSidebarOpen(false)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 overflow-y-auto">
                        {operationsItems.length > 0 && (
                            <div className="space-y-1">
                                {operationsItems.map((item) => (
                                    <NavItem
                                        key={item.path}
                                        {...item}
                                        onClick={() => setMobileSidebarOpen(false)}
                                    />
                                ))}
                            </div>
                        )}

                        {resourcesItems.length > 0 && (
                            <>
                                <SectionDivider label="Resources & Inventory" />
                                <div className="space-y-1">
                                    {resourcesItems.map((item) => (
                                        <NavItem
                                            key={item.path}
                                            {...item}
                                            onClick={() => setMobileSidebarOpen(false)}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        {configItems.length > 0 && (
                            <>
                                <SectionDivider label="Configuration" />
                                <div className="space-y-1">
                                    {configItems.map((item) => (
                                        <NavItem
                                            key={item.path}
                                            {...item}
                                            onClick={() => setMobileSidebarOpen(false)}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </nav>

                    {/* Mobile sidebar footer */}
                    <div className="p-4 border-t border-gray-100">
                        <div className="flex items-center gap-3 px-3 py-2 mb-2">
                            <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-emerald-700 font-semibold text-sm">
                                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                                </span>
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {user?.email || 'User'}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {user?.staffRole || user?.userType || 'Owner'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setMobileSidebarOpen(false);
                                handleLogout();
                            }}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all w-full border-none bg-transparent cursor-pointer"
                        >
                            <LogOut size={18} />
                            <span>Sign out</span>
                        </button>
                    </div>
                </aside>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0 min-h-0">
                {/* Top navigation — mobile/tablet only. On desktop the profile
                    block sits in the sidebar, so this row would be empty. */}
                <header className="lg:hidden h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
                    {/* Mobile menu button */}
                    <button
                        onClick={() => setMobileSidebarOpen(true)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                        <Menu size={20} />
                    </button>

                    {/* Mobile logo */}
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                            <span className="text-white font-display font-bold text-sm">W</span>
                        </div>
                    </div>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Right side — profile dropdown */}
                    <div className="relative min-w-0 max-w-[60%]">
                        <button
                            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                            className="flex items-center gap-2 w-full p-1.5 pr-2 hover:bg-gray-100 rounded-xl transition-colors bg-transparent border-none cursor-pointer"
                        >
                            <UserIdentity compact />
                            <ChevronDown
                                size={16}
                                className={`text-gray-400 flex-shrink-0 transition-transform ${
                                    profileMenuOpen ? 'rotate-180' : ''
                                }`}
                            />
                        </button>

                        {profileMenuOpen && (
                            <ProfileMenuPanel className="absolute right-0 top-full mt-2 w-56" />
                        )}
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                    {/* Shown between finishing workshop setup and signing back in. The
                        nav is empty in this state because it is built from the token's
                        uiViews claim, which was issued before the workshop existed — so
                        without this the app just looks broken. Lives in Layout rather
                        than on one page because the user can be on any route. */}
                    {needsReactivation && (
                        <div className="sticky top-0 z-30 bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-3">
                            <div className="flex items-start sm:items-center gap-3 max-w-5xl">
                                <Info size={18} className="text-amber-600 shrink-0 mt-0.5 sm:mt-0" />
                                <p className="text-sm text-amber-900 leading-relaxed flex-1">
                                    <span className="font-semibold">Your workshop is set up.</span>{' '}
                                    Sign out and sign back in to activate your account and see
                                    your full menu.
                                </p>
                                <button
                                    onClick={handleLogout}
                                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold
                                        bg-amber-600 hover:bg-amber-700 text-white border-none cursor-pointer transition-colors"
                                >
                                    <LogOut size={14} />
                                    Sign out
                                </button>
                            </div>
                        </div>
                    )}
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;